import { useEffect, useState } from 'react'
import { api } from './api'
import { Abrufausfall, useAbruf } from './Abruf'
import { fachlicherSatz } from './Fehlersatz'
import { useDarf } from './Rechte'
import { anwendername, periodenname, satz, t, tagesdatum } from './sprache'
import type { Sprachschluessel } from './sprache'
import { Spaltenmassleiste, useSpaltenmasse } from './Spaltenbreiten'
import type { Freigabeprotokollzeile, Freigabeurteil, Pruefregelzeile, Pruefstufe } from './typen'

/**
 * Die Seite „Prüfregeln festlegen" (2.13 Abschnitt 13, R910–R914).
 *
 * **Eine Zeile je Prüfung, dahinter die Stufe.** Jede Prüfung der Freigabe
 * steht hier — der Rechenkern liefert die Liste, und ein Testfall meldet eine
 * Prüfung ohne Zeile (R914). Ausgeliefert wird die Strenge von 3.1: Wer nichts
 * einstellt, arbeitet wie bisher.
 *
 * **Jeder sieht die Seite, ändern darf nur, wer das Recht hat** (R910). Wer
 * es nicht hat, liest, wie streng das System gerade ist — die Knöpfe stehen
 * dann still da, statt zu fehlen: Eine Stufe, die man nicht sieht, ist keine
 * Auskunft.
 *
 * **Eine Stufe unter der Untergrenze bietet die Reihe nicht an** (R910,
 * R1378): Ein Widerspruch läßt sich bis „Warnung“ herunterstufen, nie ohne
 * Satz übergehen; nur die Spiegel und die Wahlrechte des Anhangs dürfen bis
 * „Hinweis“ und ganz aus (R913).
 */
export function Pruefregeln({
  periode,
  perioden,
}: {
  /** Die Periode, ab der festgelegt wird — vorbelegt mit der gewählten. */
  periode: number
  /** Die Perioden der Zeitachse zur Auswahl. */
  perioden: number[]
}) {
  // Die Stufe ist zugleich Auskunft: ohne Recht steht sie still da (R910, R1180).
  const darf = useDarf()
  const [ab, setAb] = useState(periode)
  const [zeilen, setZeilen] = useState<Pruefregelzeile[] | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const { masse, rahmen } = useSpaltenmasse<HTMLTableElement>('pruefregeln:liste')
  const [schreibt, setSchreibt] = useState<string | null>(null)

  useEffect(() => {
    let gilt = true
    setFehler(null)
    api
      .pruefregeln(ab)
      .then((z) => gilt && setZeilen(z))
      // Nie ein roher Fehlertext: am Ort nur der fachliche Satz, eine Störung
      // im Störungsfenster (7.2 Abschnitt 30, R1003/R1004).
      .catch((e: unknown) => gilt && setFehler(fachlicherSatz(e)))
    return () => {
      gilt = false
    }
  }, [ab])

  async function festlegen(pruefung: string, stufe: Pruefstufe) {
    setSchreibt(pruefung)
    setFehler(null)
    try {
      setZeilen(await api.pruefregelFestlegen(pruefung, stufe, ab))
    } catch (e) {
      setFehler(fachlicherSatz(e))
    } finally {
      setSchreibt(null)
    }
  }

  const auswahl = perioden.includes(ab) ? perioden : [...perioden, ab].sort((a, b) => a - b)

  return (
    <div className="pruefregeln">
      <div className="feld">
        <label htmlFor="pruefregeln-ab">{t('pruefregeln.ab')}</label>
        <select id="pruefregeln-ab" value={ab} onChange={(e) => setAb(Number(e.target.value))}>
          {auswahl.map((p) => (
            <option key={p} value={p}>
              {periodenname(p)}
            </option>
          ))}
        </select>
      </div>

      {!darf('pruefregelFestlegen') && <p className="hinweis">{t('pruefregeln.ohnerecht')}</p>}
      {fehler && <p className="meldung">{fehler}</p>}

      {zeilen && (
        <table ref={rahmen} className="positionstabelle ziehbaretabelle">
          <Spaltenmassleiste masse={masse} />
          <thead>
            <tr>
              <th>{t('pruefregeln.spalte.pruefung')}</th>
              <th>{t('pruefregeln.spalte.stufe')}</th>
              <th>{t('pruefregeln.spalte.festgelegt')}</th>
            </tr>
          </thead>
          <tbody>
            {zeilen.map((z) => (
              <tr key={z.pruefung}>
                <td>
                  {t(`pruefung.${z.pruefung}` as Sprachschluessel)}
                  {z.ohneBewegungsarten && (
                    <div className="erlaeuterung">{t('pruefregeln.ohnebewegungsarten')}</div>
                  )}
                </td>
                <td>
                  <Stufenwahl
                    zeile={z}
                    gesperrt={!darf('pruefregelFestlegen') || schreibt !== null}
                    aufWahl={(stufe) => festlegen(z.pruefung, stufe)}
                  />
                </td>
                <td className="erlaeuterung">
                  {z.gueltigAb === null
                    ? t('pruefregeln.ausgeliefert')
                    : t('pruefregeln.seit', {
                        periode: periodenname(z.gueltigAb),
                        wer: anwendername(z.festgelegtVon ?? ''),
                        am: tagesdatum(z.festgelegtAm),
                      })}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

/**
 * **Ein Spiegel auf „Nicht prüfen" sagt es selbst** (R913) — über dem
 * Bericht, damit niemand wissen muß, daß es eine Seite *Prüfregeln* gibt.
 */
export function Ungeprueftvermerk({ spiegel, periode }: { spiegel: string; periode: number }) {
  // **Kein Vermerk heißt „wird geprüft" — das ist eine Aussage** (7.2
  // Abschnitt 39, R1211). Bis zum 2026-10-06 lief der Abruf still im
  // Hintergrund (R1007), und ein Ausfall ließ den Vermerk wortlos weg: Der
  // Spiegel sah geprüft aus. Seither ein Hauptabruf, und am Ort steht „Ließ
  // sich nicht laden" (R1210).
  const abruf = useAbruf(
    () => api.pruefregeln(periode).then((z) => z.find((r) => r.pruefung === `spiegel-${spiegel}`) ?? null),
    [spiegel, periode],
  )
  const zeile = abruf.wert

  if (abruf.ausgefallen) {
    return <Abrufausfall abruf={abruf} />
  }

  if (!zeile || zeile.stufe !== 'aus') {
    return null
  }

  return (
    <p className="meldung neutral">
      {t('pruefregeln.ungeprueft', {
        periode: periodenname(zeile.gueltigAb ?? periode),
        wer: anwendername(zeile.festgelegtVon ?? ''),
      })}
    </p>
  )
}

/**
 * Die übergangenen und ungeprüften Prüfungen (R912, R913) — der Unterpunkt
 * „Übergangene Prüfungen" im Nachweis.
 *
 * **Was übergangen wurde, steht neben dem Ergebnis** (R116): je Freigabe der
 * Befund, wie er dastand, die Stufe, die galt, wer übergangen hat und warum.
 */
export function Uebergangene() {
  // **Die Liste ist der Inhalt der Seite — ein Hauptabruf** (7.2 Abschnitt
  // 39, R1211). Bis zum 2026-10-06 stand nach einem Ausfall „nichts
  // übergangen" da.
  const abruf = useAbruf<Freigabeprotokollzeile[]>(() => api.uebergangene().then((u) => u.zeilen), [])
  const zeilen = abruf.wert
  const { masse, rahmen } = useSpaltenmasse<HTMLTableElement>('pruefregeln:uebergangene')

  return (
    <section>
      <h1>{t('bereich.nachweis.uebergangene')}</h1>
      {abruf.ausgefallen && <Abrufausfall abruf={abruf} />}
      {zeilen === null && !abruf.ausgefallen && <p className="hinweis">{t('kontext.rechnet')}</p>}
      {zeilen?.length === 0 && <p className="hinweis">{t('uebergangene.leer')}</p>}
      {zeilen && zeilen.length > 0 && (
        <table ref={rahmen} className="positionstabelle ziehbaretabelle">
          <Spaltenmassleiste masse={masse} />
          <thead>
            <tr>
              <th>{t('uebergangene.spalte.abschluss')}</th>
              <th>{t('pruefregeln.spalte.pruefung')}</th>
              <th>{t('uebergangene.spalte.befund')}</th>
              <th>{t('uebergangene.spalte.begruendung')}</th>
            </tr>
          </thead>
          <tbody>
            {zeilen.map((z) => (
              <tr key={`${z.laufId}-${z.pruefung}`}>
                <td>
                  {periodenname(z.periode)}
                  <div className="erlaeuterung">
                    {t('uebergangene.wer', { wer: anwendername(z.erfasstVon), am: tagesdatum(z.erfasstAm) })}
                  </div>
                </td>
                <td>
                  {t(`pruefung.${z.pruefung}` as Sprachschluessel)}
                  <div className="erlaeuterung">{t(`pruefregeln.stufe.${z.stufe}` as Sprachschluessel)}</div>
                </td>
                <td>{z.meldung ? satz(z.meldung) : t('uebergangene.nichtgeprueft')}</td>
                {/* Ein Hinweis geht ohne Satz durch — dass er übergangen wurde, steht trotzdem da (R1377). */}
                <td>{z.begruendung ?? (z.angeschlagen && z.stufe === 'hinweis' ? t('uebergangene.ohnekommentar') : '')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  )
}

/** Die vier Stufen von der mildesten zur strengsten (R1377). */
export const STUFEN: Pruefstufe[] = ['aus', 'hinweis', 'warnung', 'sperrt']

/**
 * **Einen Satz verlangt nur die Warnung** (R912, R1377) — ein Hinweis lässt
 * sich kommentarlos übergehen. Ohne bekannte Stufe gilt die strengere Lesart,
 * wie im Freigabedienst.
 */
export function verlangtSatz(urteil: Freigabeurteil, code: string): boolean {
  return (urteil.stufen?.[code] ?? 'warnung') !== 'hinweis'
}

/** Ob eine Stufe auf der Seite angeboten wird — nie unter der Untergrenze (R910). */
export function angeboten(stufe: Pruefstufe, niedrigste: Pruefstufe): boolean {
  return STUFEN.indexOf(stufe) >= STUFEN.indexOf(niedrigste)
}

function Stufenwahl({
  zeile,
  gesperrt,
  aufWahl,
}: {
  zeile: Pruefregelzeile
  gesperrt: boolean
  aufWahl: (stufe: Pruefstufe) => void
}) {
  return (
    <div className="knopfreihe" role="radiogroup" aria-label={t(`pruefung.${zeile.pruefung}` as Sprachschluessel)}>
      {STUFEN.filter((s) => angeboten(s, zeile.niedrigste)).map((s) => (
        <button
          key={s}
          type="button"
          role="radio"
          aria-checked={zeile.stufe === s}
          className={zeile.stufe === s ? 'aktiv' : ''}
          data-name={t(`pruefregeln.stufe.${s}` as Sprachschluessel)}
          disabled={gesperrt}
          onClick={() => zeile.stufe !== s && aufWahl(s)}
        >
          {t(`pruefregeln.stufe.${s}` as Sprachschluessel)}
        </button>
      ))}
    </div>
  )
}
